<template>

    <v-field v-if="!novalidate"
             v-bind="$componentProps('v-field', ['label', 'as', 'name'])"
             :label="validationLabel" :name="validationName"
             v-slot="validationResult" ref="validationProviderRef">

        <div class="ui-form-item" v-bind="$attrs">

            <div class="before" v-if="title || subtitle || $slots.title">

                <slot name="title">
                    <span class="title" v-if="title">
                        {{ title }}
                    </span>
                </slot>

                <span class="subtitle" v-if="subtitle">{{ subtitle }}</span>
            </div>

            <div class="element">
                <slot v-bind="validationResult"></slot>
            </div>

            <div class="after" v-if="hint || (validationResult.errors.length && !silent)">
                <span class="error" v-if="validationResult.errors.length && !silent">
                    {{ resolveErrors(validationResult.errors) }}
                </span>
                <span class="hint" v-else-if="hint">{{ hint }}</span>
            </div>

        </div>

    </v-field>

    <div v-else class="ui-form-item">

        <div class="before" v-if="title || subtitle || $slots.title">

            <slot name="title">
                <span class="title" v-if="title">
                    {{ title }}
                </span>
            </slot>

            <span class="subtitle" v-if="subtitle">{{ subtitle }}</span>
        </div>

        <div class="element">
            <slot></slot>
        </div>

        <div class="after" v-if="hint || errorText">
            <span class="error" v-if="errorText">{{ errorText }}</span>
            <span class="hint" v-else-if="hint">{{ hint }}</span>
        </div>

    </div>

</template>

<script lang="ts">

import { Component, Prop, Ref, Vue } from 'vue-facing-decorator';

import { compact, isArray } from 'lodash';

import type { ComponentProps } from '@wittignl/js-core';
import { componentPropsMixin, createComponentMixin, lcFirstFilter, ucFirstFilter, uuid } from '@wittignl/js-core';

import { Field as VValidateField, Field, RuleExpression } from 'vee-validate';

const vValidateFieldMixin = {
    props: VValidateField.props
}

@Component({
    name: 'ui-form-item',
    components: {
        'v-field': Field
    },
    mixins: [componentPropsMixin, vValidateFieldMixin]
})
export default class FormItem extends Vue {

    @Prop({ type: Boolean })
    novalidate: boolean;

    @Prop({ type: Boolean })
    silent: boolean;

    @Prop({ type: String })
    errorText: string;

    @Prop({ type: String })
    title: string;

    @Prop({ type: String })
    subtitle: string;

    @Prop({ type: String })
    hint: string;

    @Prop({ type: String })
    label: string;

    @Prop({ type: String })
    name: string

    @Ref
    validationProviderRef: any;

    resolveErrors(errors) {

        const errorString = isArray(errors) ? compact(errors).join(' ') : errors;
        return ucFirstFilter(errorString);
    }

    syncValue(value) {

        return this.validationProviderRef.syncValue(value);
    }

    get validationLabel() {

        if(this.label){

            return this.label;
        }
        else if(this.title){

            return lcFirstFilter(this.title);
        }

        return this.name;
    }

    get validationName() {

        return this.name || uuid();
    }
}

export type FormItemComp = InstanceType<typeof FormItem>
export type FormItemProps = ComponentProps<FormItemComp> & {
    label: string;
    as: string | Record<string, any>;
    bails: boolean;
    uncheckedValue: any;
    validateOnInput: boolean;
    validateOnChange: boolean;
    validateOnBlur: boolean;
    validateOnModelUpdate: boolean;
    modelValue: any;
    validateOnMount: boolean;
    standalone: boolean;
    modelModifiers: any;
    rules: RuleExpression<unknown>;
    'onUpdate:modelValue': (e: any) => unknown;
    keepValue: boolean;
}

export const formItemCompMixin = createComponentMixin(FormItem, vValidateFieldMixin)

</script>
<style src="../../../scss/component/form/FormItem.scss" lang="scss"></style>